import React, { createContext, useContext, useMemo } from 'react';
import { UseFormReturnType } from '@mantine/form';
import {
  BulkClaimForm,
  BulkEditClaimForm,
  CreateClaimForm,
  EditClaimForm,
} from 'interfaces';

export type FormType = 'create' | 'edit' | 'bulk' | 'individual' | 'bulkEdit';

export interface ClaimFormContextValue {
  formType: FormType;
  prefix?: string;
  siteID?: string;
  createForm?: UseFormReturnType<CreateClaimForm>;
  editForm?: UseFormReturnType<EditClaimForm>;
  bulkForm?: UseFormReturnType<BulkClaimForm>;
  bulkEditForm?: UseFormReturnType<BulkEditClaimForm>;
}

const ClaimFormContext = createContext<ClaimFormContextValue | null>(null);

export const ClaimFormProvider: React.FC<{
  form: ClaimFormContextValue;
  children: React.ReactNode;
}> = ({ form, children }) => {
  return (
    <ClaimFormContext.Provider value={form}>
      {children}
    </ClaimFormContext.Provider>
  );
};

export const useClaimFormContext = () => {
  const context = useContext(ClaimFormContext);
  if (!context) {
    throw new Error(
      'useClaimFormContext must be used within a ClaimFormProvider'
    );
  }
  return context;
};

export function useActiveForm() {
  const {
    formType,
    createForm,
    editForm,
    bulkForm,
    bulkEditForm,
    prefix,
    siteID,
  } = useClaimFormContext();

  const activeForm: UseFormReturnType<any> | null = useMemo(() => {
    switch (formType) {
      case 'edit':
        return editForm;
      case 'create':
        return createForm;
      case 'bulkEdit':
        return bulkEditForm;
      case 'bulk':
      case 'individual':
        return bulkForm;
      default:
        return null;
    }
  }, [formType, createForm, editForm, bulkForm, bulkEditForm, prefix]);
  return {
    form: activeForm,
    prefix: prefix,
    siteID,
    isBulk:
      formType === 'bulk' ||
      formType === 'individual' ||
      formType === 'bulkEdit',
    formType: formType,
  };
}
